import React from "react";
import Select, { components, MultiValue } from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";

export interface TagOption {
	value: number | string;
	label: string;
}

interface TagSelectorProps {
	value: TagOption[];
	onChange: (tags: TagOption[]) => void;
	fetchSuggestions?: (input: string) => Promise<TagOption[]>;
	placeholder?: string;
	options?: TagOption[];
	components?: typeof components;
	className?: string;
	allowCustomTags?: boolean;
	isSearchable?: boolean;
}

const TagSelector: React.FC<TagSelectorProps> = ({
	value,
	onChange,
	fetchSuggestions,
	placeholder = "Type to add or select tags",
	options = [],
	components,
	className = "",
	allowCustomTags = false,
	isSearchable = true
}) => {
	const loadOptions = async (input: string) => {
		if (fetchSuggestions) {
			return await fetchSuggestions(input);
		}
		return options.filter((opt) =>
			opt.label.toLowerCase().includes(input.toLowerCase())
		);
	};

	const handleChange = (selected: MultiValue<TagOption>) => {
		onChange(Array.isArray(selected) ? [...selected] : []);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (!allowCustomTags && event.key === "Enter") {
			// Prevent form submission when Enter is pressed in the select
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const SelectComponent = allowCustomTags ? AsyncCreatableSelect : Select;

	return (
		<SelectComponent
			isMulti
			isClearable
			isSearchable={isSearchable}
			cacheOptions
			defaultOptions={options}
			loadOptions={allowCustomTags ? loadOptions : undefined}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			value={value}
			options={!allowCustomTags ? options : undefined}
			placeholder={placeholder}
			components={components}
			className={`${className} react-select-container`}
			classNamePrefix="react-select"
			formatCreateLabel={(inputValue: string) => `Add "${inputValue}"`}
		/>
	);
};

export default TagSelector;
